import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLoader } from '../../../providers'
import { useNirService } from '../../../services'
import { useRoles } from '../../../utils/hooks/useRoles'
import { RootState } from '../../../store'
import {
  UPLOAD_MESSAGES,
  handleStreamProgress,
  showCompletionToast,
  closeController,
  getEventMessage,
  isCompletionEvent,
  isErrorEvent,
} from './utils'
import { StreamEvent, StreamController } from './types'

const PREFIXES = ['wheat', 'flour'] as const

export const GlobalUploadReconnect = () => {
  const { showLoader, hideLoader, setLoaderText, setProgress } = useLoader()
  const { openNirUploadStream: openWheatStream } = useNirService()
  const { openNirUploadStream: openFlourStream } = useNirService('flour')
  const { isSuperAdmin } = useRoles()
  const profile = useSelector((state: RootState) => state.user.profile)

  const activeControllersRef = useRef<Map<string, StreamController>>(new Map())
  const loaderControls = { setLoaderText, setProgress }

  const cleanup = () => {
    activeControllersRef.current.forEach((controller, loaderId) => {
      closeController(controller)
      hideLoader(loaderId)
    })
    activeControllersRef.current.clear()
  }

  useEffect(() => {
    if (!isSuperAdmin || !profile) {
      cleanup()
      return
    }

    const reconnect = (prefix: 'wheat' | 'flour') => {
      const jobId = localStorage.getItem(`${prefix}_nir_job_id`)
      if (!jobId) return

      const loaderId = `${prefix}-upload-reconnect`
      const openStream = prefix === 'wheat' ? openWheatStream : openFlourStream

      showLoader(`Reconnecting to ${prefix} upload...`, loaderId, prefix)

      const cleanupStream = () => {
        const controller = activeControllersRef.current.get(loaderId)
        closeController(controller || null)
        activeControllersRef.current.delete(loaderId)
        hideLoader(loaderId)
      }

      const controller = openStream(jobId, {
        onOpen: () => setLoaderText(UPLOAD_MESSAGES.reconnected, loaderId),
        onEvent: (evt: StreamEvent) => {
          const type = evt?.type?.toLowerCase()
          const message =
            getEventMessage(evt) || UPLOAD_MESSAGES.defaultComplete

          // Handle progress updates
          handleStreamProgress(evt, loaderControls, loaderId)

          // Handle terminal events
          if (isCompletionEvent(type)) {
            const isPartialSuccess = evt?.status === false
            showCompletionToast(message, isPartialSuccess)
            localStorage.removeItem(`${prefix}_nir_job_id`)
            cleanupStream()
          } else if (isErrorEvent(type)) {
            console.error(message || UPLOAD_MESSAGES.defaultFailed)
            localStorage.removeItem(`${prefix}_nir_job_id`)
            cleanupStream()
          }
        },
        onError: (err: any) => {
          console.error(`Stream error during ${prefix} recovery`, err)
          if (err?.status === 404 || err?.message?.includes('404')) {
            localStorage.removeItem(`${prefix}_nir_job_id`)
          }
          cleanupStream()
        },
      })

      activeControllersRef.current.set(loaderId, controller)
    }

    PREFIXES.forEach(reconnect)

    return cleanup
  }, [isSuperAdmin, profile?.company?.id])

  return null
}
